<template>
	<div>
		<HeaderDropdown no-caret left>
			<template slot="header">
				<b-nav-item :class="[notifAnimator, 'pl-2', 'pr-3']">
					<span class="bell">
						<i class="icon-bell"></i>
					</span>
					<b-badge v-if="unreadCount" pill variant="danger">{{ unreadCount }}</b-badge>
				</b-nav-item>
			</template>
			<template slot="dropdown">
				<div class="notification-dropdown-panel" :style="{ display: 'block' }">
					<div class="notification-panel-header">
						<div class="notification-panel-title">
							<b-icon-bell-fill class="notification-panel-icon" />
							<strong v-if="unreadCount === 0">No unread messages</strong>
							<strong v-else>{{ unreadCount }} unread messages</strong>
						</div>
						<button class="notification-close-btn" type="button" aria-label="Close notifications">&times;</button>
					</div>
					<div v-if="notifications.length" class="notification-toolbar">
						<b-form-checkbox :checked="allVisibleSelected" @change="toggleAllVisible">
							Select visible
						</b-form-checkbox>
						<b-button size="sm" variant="outline-danger" class="clear-selected-btn"
							:disabled="selectedNotificationIds.length === 0 || isClearing" @click="clearSelected">
							Clear Selected
						</b-button>
					</div>
					<vue-custom-scrollbar class="notif-box">
						<template v-if="notifications.length">
							<div v-for="group in groupedNotifications" :key="group.key" class="notification-timeline-section">
								<div class="notification-date-row">
									<span class="notification-date-dot"></span>
									<div class="notification-date-label">
										<span>{{ group.relativeLabel }}</span>
										<span v-if="group.dateLabel" class="notification-date-separator">&bull;</span>
										<span v-if="group.dateLabel">{{ group.dateLabel }}</span>
									</div>
									<b-button size="sm" variant="link" class="clear-group-btn" :disabled="isClearing"
										@click="clearGroup(group)">
										Clear all
									</b-button>
								</div>
								<div class="notification-group-items">
									<NotificationItem v-for="item in group.items" :key="item.id" :item="item" selectable
										:selected="isSelected(item.id)" @toggle-selected="toggleSelected" @clear="clearNotification"
										@viewed="markNotificationViewed" />
								</div>
							</div>
						</template>
						<b-alert v-else show>
							<i class="fa fa-check-circle"></i>&nbsp; You have no messages today.
						</b-alert>
						<div v-if="hasMore" class="text-center pt-2">
							<b-button size="sm" variant="outline-primary" :disabled="isLoading" @click="loadMoreNotifications">
								Show more
							</b-button>
						</div>
					</vue-custom-scrollbar>
				</div>
			</template>
		</HeaderDropdown>

		<ForceLogoutConfirmation />
		<NotificationDispatchDetails />
		<NotificationIncidentDetails />
	</div>
</template>

<script>
// Component
import NotificationItem from './NotificationItem';
import ForceLogoutConfirmation from '../../account/ForceLogoutConfirmation';
import NotificationDispatchDetails from './NotificationDispatchDetails';
import NotificationIncidentDetails from './NotificationIncidentDetails';

// API
import notificationDAO from '@/database/notifications';
import userSessionsDAO from '@/database/userSessions';

// Others
import { BIconBellFill } from 'bootstrap-vue';
import { HeaderDropdown } from '@coreui/vue';
import { firebase } from '@/config/firebase';
import { DateUtil } from '@/utils/dateutil';
import vueCustomScrollbar from 'vue-custom-scrollbar'
import "vue-custom-scrollbar/dist/vueScrollbar.css"

export default {
	name: 'Notifications',
	components: {
		HeaderDropdown,
		NotificationItem,
		BIconBellFill,
		ForceLogoutConfirmation,
		NotificationDispatchDetails,
		NotificationIncidentDetails,
		vueCustomScrollbar
	},
	data() {
		return {
			notifications: [],
			loggedUser: this.$store.getters.loggedUser,
			cachedIdToken: '',
			notificationCursor: null,
			hasMore: false,
			isLoading: false,
			isClearing: false,
			selectedNotifications: {},

			// Listeners
			notificationListener: '',
			userSessionListener: '',
		};
	},
	computed: {
		unreadCount() {
			return this.notifications.filter((item) => item.isViewed === false).length;
		},
		notifAnimator() {
			if (this.unreadCount > 0) {
				return 'animated infinite tada';
			}
		},
		selectedNotificationIds() {
			return Object.keys(this.selectedNotifications).filter(id => this.selectedNotifications[id]);
		},
		allVisibleSelected() {
			return this.notifications.length > 0 && this.notifications.every(item => this.selectedNotifications[item.id]);
		},
		groupedNotifications() {
			return this.notifications.reduce((groups, item) => {
				const dateKey = DateUtil.roundDate(item.dateSent);
				const existingGroup = groups.find(group => group.key === dateKey);

				if (existingGroup) {
					existingGroup.items.push(item);
					return groups;
				}

				return [
					...groups,
					{
						key: dateKey,
						...this.getDateGroupLabel(item.dateSent),
						items: [item],
					},
				];
			}, []);
		},
	},
	mounted() {
		let email = this.loggedUser.emailAddress;
		this.loadNotifications({ reset: true });

		this.cachedIdToken = localStorage.getItem('token');
		if (this.cachedIdToken && this.cachedIdToken.length > 0) {
			this.userSessionListener = userSessionsDAO.getUserSessionListener({ email }, this.sessionListenerCallback);
		}
	},
	methods: {
		async loadNotifications({ reset = false, limit = 20 } = {}) {
			if (this.isLoading) {
				return;
			}

			try {
				this.isLoading = true;
				const email = this.loggedUser.emailAddress;
				const result = await notificationDAO.getNotificationsPage({
					email
				}, {
					limit,
					cursor: reset ? null : this.notificationCursor
				});

				const visibleNotifications = result.notifications.filter(notification => this.isWebNotification(notification));
				this.notifications = reset ? visibleNotifications : [...this.notifications, ...visibleNotifications];
				this.notificationCursor = result.cursor;
				this.hasMore = result.hasMore;
			} catch (error) {
				this.$toaster.error('Error loading notifications. Please reload the page again.');
			} finally {
				this.isLoading = false;
			}
		},
		loadMoreNotifications() {
			return this.loadNotifications({ limit: 10 });
		},
		isWebNotification(notification) {
			const rules = notification.rules || {};
			return rules.deviceType === 'web' || rules.deviceType === 'hybrid';
		},
		getDateGroupLabel(dateSent) {
			const sentDate = new Date(dateSent);
			const yesterday = new Date();
			yesterday.setDate(yesterday.getDate() - 1);
			const formattedDate = DateUtil.getFormattedDateShorten(dateSent);

			if (DateUtil.isDateToday(dateSent)) {
				return {
					relativeLabel: 'Today',
					dateLabel: formattedDate,
				};
			}

			if (
				sentDate.getFullYear() === yesterday.getFullYear() &&
				sentDate.getMonth() === yesterday.getMonth() &&
				sentDate.getDate() === yesterday.getDate()
			) {
				return {
					relativeLabel: 'Yesterday',
					dateLabel: formattedDate,
				};
			}

			return {
				relativeLabel: formattedDate,
				dateLabel: '',
			};
		},
		isSelected(id) {
			return this.selectedNotifications[id] === true;
		},
		toggleSelected(id, selected) {
			this.$set(this.selectedNotifications, id, selected);
		},
		toggleAllVisible(selected) {
			this.notifications.forEach(item => {
				this.$set(this.selectedNotifications, item.id, selected);
			});
		},
		async clearNotification(notification) {
			const confirmed = await this.confirmClear({
				message: 'Clear this notification? This will remove it from your notification list.',
				okTitle: 'Clear Notification',
			});

			if (confirmed) {
				await this.clearNotifications([notification.id]);
			}
		},
		async clearGroup(group) {
			const confirmed = await this.confirmClear({
				message: `Clear all notifications for ${group.relativeLabel}${group.dateLabel ? ` ${group.dateLabel}` : ''}? This will remove them from your notification list.`,
				okTitle: 'Clear All',
			});

			if (confirmed) {
				await this.clearNotifications(group.items.map(item => item.id));
			}
		},
		markNotificationViewed(id) {
			this.notifications = this.notifications.map(notification => {
				return notification.id === id ? { ...notification, isViewed: true } : notification;
			});
		},
		async clearSelected() {
			const selectedCount = this.selectedNotificationIds.length;
			const confirmed = await this.confirmClear({
				message: `Clear ${selectedCount} selected notification${selectedCount === 1 ? '' : 's'}? This will remove them from your notification list.`,
				okTitle: 'Clear Selected',
			});

			if (confirmed) {
				await this.clearNotifications(this.selectedNotificationIds);
			}
		},
		async confirmClear({ message, okTitle }) {
			return this.$bvModal.msgBoxConfirm(message, {
				title: 'Confirm Clear',
				size: 'sm',
				okVariant: 'danger',
				okTitle,
				cancelTitle: 'Cancel',
				centered: true,
			});
		},
		async clearNotifications(ids) {
			if (!ids.length || this.isClearing) {
				return;
			}

			try {
				this.isClearing = true;
				await notificationDAO.clearNotifications(ids);
				this.notifications = this.notifications.filter(item => !ids.includes(item.id));
				ids.forEach(id => this.$delete(this.selectedNotifications, id));
			} catch (error) {
				this.$toaster.error('Error clearing notifications. Please try again.');
			} finally {
				this.isClearing = false;
			}
		},
		sessionListenerCallback(type, userSession) {
			const user = firebase.auth().currentUser;
			if (userSession.idToken !== this.cachedIdToken && user !== null && user.email === userSession.emailAddress) {
				this.$bvModal.show('force-logout-confirmation');
			}
		}
	},

	destroyed() {
		if (typeof this.notificationListener === 'function') {
			this.notificationListener();
		}
		if (typeof this.userSessionListener === 'function') {
			this.userSessionListener();
		}
	}
};
</script>

<style lang="scss" scoped>
@import '~animate.css';

.chat-box-holder {
	background-color: #f9f9fa;
	padding: 10px 10px;
	width: 400px;
	max-height: 80vh;
}

.notif-list-container {
	max-height: 500px;
}

.bell {
	color: #024F98;
}

.nav-item {
	min-width: 30px;
}

.notification-dropdown-panel {
	max-width: calc(100vw - 24px);
	width: 490px;
	background: #ffffff;
	border-radius: 4px;
	box-shadow: 0 16px 36px rgba(15, 35, 55, 0.24), 0 2px 8px rgba(15, 35, 55, 0.14);
	overflow: hidden;
}

.notification-panel-header {
	align-items: center;
	background: #063f7d;
	border-bottom: none;
	color: #ffffff;
	display: flex;
	justify-content: space-between;
	min-height: 60px;
	padding: 15px 18px;
	width: 100%;
}

.notification-panel-header strong {
	display: block;
	font-size: 16px;
	font-weight: 700;
	line-height: 1.25;
}

.notification-panel-title {
	align-items: center;
	display: flex;
	gap: 12px;
	min-width: 0;
	width: 100%;
}

.notification-panel-icon {
	flex: 0 0 auto;
	font-size: 19px;
}

.notification-close-btn {
	background: transparent;
	border: 0;
	color: #ffffff;
	font-size: 28px;
	font-weight: 300;
	line-height: 1;
	margin-left: 16px;
	padding: 0;
}

.notif-box {
	position: relative;
	padding: 12px 14px 14px 22px;
	height: 700px;
	background: #ffffff;
}

.notification-toolbar {
	align-items: center;
	display: grid;
	gap: 12px;
	grid-template-columns: minmax(0, 1fr) auto;
	padding: 14px 14px;
	background: #ffffff;
	border-bottom: 1px solid #e6ebf2;
	width: 100%;
}

.clear-selected-btn {
	justify-self: end;
	margin-left: auto;
}

.notification-timeline-section {
	position: relative;
	padding-left: 20px;
}

.notification-timeline-section::before {
	background: #cbd7e3;
	bottom: 0;
	content: '';
	left: 6px;
	position: absolute;
	top: 23px;
	width: 2px;
}

.notification-date-row {
	align-items: center;
	display: flex;
	gap: 8px;
	margin: 2px 0 9px -20px;
	position: relative;
	z-index: 1;
}

.notification-date-dot {
	background: #034f99;
	border: 2px solid #ffffff;
	border-radius: 50%;
	box-shadow: 0 0 0 1px rgba(2, 79, 152, 0.24);
	display: block;
	flex: 0 0 auto;
	height: 14px;
	width: 14px;
}

.notification-date-label {
	align-items: center;
	color: #31485f;
	display: flex;
	flex: 1;
	font-size: 13px;
	font-weight: 700;
	gap: 5px;
	line-height: 1.3;
	margin: 0;
}

.notification-date-separator {
	color: #7d8b99;
	font-size: 12px;
}

.notification-group-items {
	position: relative;
	z-index: 1;
}

.clear-group-btn {
	color: #1681ff;
	font-size: 12px;
	font-weight: 600;
	padding: 0;
}

.clear-group-btn:hover {
	color: #006fda;
	text-decoration: none;
}
</style>
